import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Eye, RefreshCw } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router";
import type { ProductDto } from "../../../shared/api-types";
import { flagSeverity, portionTotal, type ReviewFlag, reviewText } from "../../../shared/import-merge";
import { formatPriceInput } from "../../../shared/money";
import { ApiRequestError } from "../../api/client";
import {
  importApi,
  keys,
  menuApi,
  openPreview,
  useDraft,
  useImports,
  usePublishPreview,
} from "../../api/queries";
import { FlowSteps } from "../../components/FlowSteps";
import { CHIPS, StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { plural, price } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { ConfirmDialog } from "../menus/dialogs";
import { FrozenChecklist } from "./FrozenChecklist";
import { RetakePages } from "./RetakePages";

// "Verifică" (spec §1A, ARCHITECTURE §7.3): one screen after an import. Owner, 2026-10-23: only decisions that
// matter for the menu's correctness are asked. Critical ones (prices) block publishing and come first, each with
// its one-click resolution; "to check" ones (a name read two ways, an uncertain portion, a possible duplicate)
// never block; what the AI fixed by itself is in a history. Everything else is confirmed collectively (the
// restaurant's explicit responsibility, recorded per product); then a single "Publică meniul".

const critical = (f: ReviewFlag) => flagSeverity(f) === "critical";
/** The question to show first: a critical one before one that does not block. */
const firstFlag = (p: ProductDto) => (p.reviewFlags.find(critical) ?? p.reviewFlags[0]) as ReviewFlag;
const isCritical = (p: ProductDto) => p.reviewFlags.some(critical);
const pageImageUrl = (rid: string, p: ProductDto) =>
  p.importSource?.jobId && p.importSource.fileId
    ? `/api/restaurants/${rid}/imports/${p.importSource.jobId}/files/${p.importSource.fileId}/image`
    : null;

function ExceptionRow({
  p,
  categoryName,
  menuId,
  busy,
  onAct,
  linkHost = null,
}: {
  p: ProductDto;
  categoryName: string;
  menuId: string;
  busy: boolean;
  onAct: (fn: () => Promise<unknown>, done: string) => void;
  /** Imported from a link (owner 2026-11-04): its site; there is no page photo to compare with. */
  linkHost?: string | null;
}) {
  const { membership } = useOutletContext<ShellContext>();
  const api = menuApi(membership.restaurant.id);
  const cur = membership.restaurant.currency;
  const [typed, setTyped] = useState("");
  // Removing an imported product deletes it from the draft for good (it was never published): ask first.
  const [removing, setRemoving] = useState<string | null>(null);
  const flag = firstFlag(p);
  const pageUrl = linkHost ? null : pageImageUrl(membership.restaurant.id, p);
  const fragment = p.importSource?.text || ("raw" in flag ? flag.raw : "");
  const sizes = "sizes" in flag && flag.sizes?.length ? flag.sizes : null;
  const setPrice = (value: string) =>
    onAct(
      () => api.updatePrice(menuId, p.id, value),
      `${p.name}: preț ${value} ${cur === "EUR" ? "€" : "lei"} în ciornă.`,
    );
  // The portion is whatever the restaurant confirms: as printed, only the printed total, or typed by hand.
  const setPortion = (value: string) =>
    onAct(() => api.resolvePortion(menuId, p.id, value), `${p.name}: gramaj „${value}” în ciornă.`);
  // The name as it is on the menu: one of the readings, or typed by the restaurant.
  const setName = (value: string) =>
    onAct(() => api.resolveName(menuId, p.id, value), `Nume confirmat: „${value}”.`);
  const total = flag.code === "portion_unclear" ? portionTotal(flag.raw) : null;

  return (
    <li className="verify-row">
      <div className="stack" style={{ gap: 4, minWidth: 0 }}>
        <div className="row" style={{ gap: 8 }}>
          <span style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{p.name}</span>
          <StatusChip {...CHIPS[flag.code]} />
        </div>
        <span className="caption muted">
          {categoryName}
          {linkHost
            ? ` · pe ${linkHost}: „${fragment}”`
            : `${fragment ? ` · în document: „${fragment}”` : ""}${p.importSource ? ` · pagina ${p.importSource.page}` : ""}`}
        </span>
        <span className="note">
          {linkHost && flag.code === "price_unverified"
            ? `Preț luat de pe ${linkHost}. Aplicațiile de livrare au adesea prețuri mai mari decât în restaurant: confirmă-l sau corectează-l.`
            : reviewText(flag)}
        </span>
        {pageUrl && (
          <a className="caption" href={pageUrl} target="_blank" rel="noopener">
            Vezi pagina {p.importSource?.page} (fotografia trimisă)
          </a>
        )}
      </div>
      <div className="row" style={{ gap: 8 }}>
        {flag.code === "name_unclear" ? (
          <>
            {[flag.raw, ...flag.candidates].map((n) => (
              <button
                key={n}
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => setName(n)}
              >
                {flag.candidates.length ? n : "E în meniu, numele e corect"}
              </button>
            ))}
            <form
              className="row"
              style={{ gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                if (typed.trim()) setName(typed.trim());
              }}
            >
              <label className="sr-only" htmlFor={`name-${p.id}`}>
                {`Numele corect pentru ${p.name}`}
              </label>
              <input
                id={`name-${p.id}`}
                className="field-input"
                style={{ flex: "1 1 160px", maxWidth: 220, minWidth: 0, height: 40 }}
                maxLength={150}
                placeholder="Numele din meniu"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !typed.trim()}>
                Salvează
              </button>
            </form>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setRemoving(`„${p.name}” eliminat: nu este în meniu.`)}
            >
              Nu e în meniu
            </button>
          </>
        ) : flag.code === "import_missing" ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() => onAct(() => api.keepDuplicate(menuId, p.id), `„${p.name}” rămâne în meniu.`)}
            >
              Păstrează în meniu
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setRemoving(`„${p.name}” scos din meniu (apare ca eliminat la publicare).`)}
            >
              Scoate din meniu
            </button>
          </>
        ) : flag.code === "import_new" || flag.code === "import_changed" ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={() => onAct(() => api.keepDuplicate(menuId, p.id), `„${p.name}”: văzut.`)}
          >
            Am văzut, e corect
          </button>
        ) : flag.code === "possible_duplicate" ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() =>
                onAct(() => api.keepDuplicate(menuId, p.id), `„${p.name}” păstrat ca produs separat.`)
              }
            >
              Păstrează ambele
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setRemoving(`„${p.name}” eliminat din ciornă.`)}
            >
              Elimină acest produs
            </button>
          </>
        ) : flag.code === "portion_unclear" ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() => setPortion(p.portion)}
            >
              E corect așa
            </button>
            {total && total !== p.portion && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => setPortion(total)}
              >
                Doar totalul: {total}
              </button>
            )}
            <form
              className="row"
              style={{ gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                if (typed.trim()) setPortion(typed.trim());
              }}
            >
              <label className="sr-only" htmlFor={`portion-${p.id}`}>
                {`Gramaj corect pentru ${p.name}`}
              </label>
              <input
                id={`portion-${p.id}`}
                className="field-input"
                style={{ flex: "1 1 140px", maxWidth: 170, minWidth: 0, height: 40 }}
                maxLength={40}
                placeholder="ex. 350 g (200/150 g)"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !typed.trim()}>
                Salvează
              </button>
            </form>
          </>
        ) : sizes ? (
          <Link className="btn btn-secondary btn-sm" to={`/app/products?menu=${menuId}`}>
            Completează prețurile pe mărimi ({sizes.join(", ")})
          </Link>
        ) : (
          <>
            {flag.code === "price_unverified" &&
              flag.candidates.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy}
                  onClick={() => setPrice(formatPriceInput(c))}
                >
                  E corect: {price(c, cur)}
                </button>
              ))}
            {flag.code === "price_ambiguous" &&
              flag.candidates.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => setPrice(formatPriceInput(c))}
                >
                  {price(c, cur)}
                </button>
              ))}
            <form
              className="row"
              style={{ gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                if (typed.trim()) setPrice(typed.trim());
              }}
            >
              <label className="sr-only" htmlFor={`price-${p.id}`}>
                {flag.code === "price_missing" ? `Preț pentru ${p.name}` : `Alt preț pentru ${p.name}`}
              </label>
              <input
                id={`price-${p.id}`}
                className="field-input"
                style={{ flex: "1 1 96px", maxWidth: 110, minWidth: 0, height: 40 }}
                inputMode="decimal"
                placeholder={flag.code === "price_missing" ? "ex. 42,00" : "Alt preț"}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !typed.trim()}>
                Salvează
              </button>
            </form>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setRemoving(`„${p.name}” eliminat din ciornă.`)}
            >
              Elimină
            </button>
          </>
        )}
      </div>
      {removing && (
        <ConfirmDialog
          title={`Elimini „${p.name}” din ciornă?`}
          body={
            flag.code === "import_missing"
              ? "Produsul iese din meniu la următoarea publicare. Până atunci îl poți readuce din Produse și categorii (produse eliminate)."
              : "Produsul se șterge din ciorna importată și nu poate fi recuperat. Dacă e pe meniu, îl poți adăuga din nou manual."
          }
          confirmLabel="Elimină produsul"
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            const done = removing;
            setRemoving(null);
            onAct(() => api.deleteProduct(menuId, p.id), done);
          }}
        />
      )}
    </li>
  );
}

export function VerifyPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const api = menuApi(rid);
  const jobs = useImports(rid);
  const job = jobs.data?.find((j) => j.id === jobId);
  const menuId = job?.menuId ?? null;
  const draft = useDraft(rid, menuId);
  const preview = usePublishPreview(rid, menuId);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [agreeTr, setAgreeTr] = useState(false);
  const [showOk, setShowOk] = useState(false);
  // Statements the restaurant confirms before publishing (allergens, frozen products), one tick each.
  const [acks, setAcks] = useState<ReadonlySet<string>>(new Set());
  const [showImportant, setShowImportant] = useState(false);
  const [showFixes, setShowFixes] = useState(false);
  const publishing = useRef(false);
  const publishKey = useRef<{ revision: number; key: string } | null>(null);

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.imports(rid) }),
      qc.invalidateQueries({ queryKey: keys.menus(rid) }),
      ...(menuId
        ? [
            qc.invalidateQueries({ queryKey: keys.draft(rid, menuId) }),
            qc.invalidateQueries({ queryKey: keys.preview(rid, menuId) }),
          ]
        : []),
    ]);

  async function act(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  }

  const header = (
    <div className="stack" style={{ gap: 12 }}>
      <FlowSteps current={1} />
      <h1 className="h1">Verifică meniul</h1>
    </div>
  );

  if (jobs.isPending || (menuId && (draft.isPending || preview.isPending))) return header;
  if (!job || !menuId || !draft.data) {
    return (
      <>
        {header}
        <Alert tone="info">
          Ciorna acestui import nu este pregătită încă. <Link to="/app/imports">Înapoi la import</Link>
        </Alert>
      </>
    );
  }

  const d = draft.data;
  const catName = new Map(d.categories.map((c) => [c.id, c.name]));
  const exceptions = d.products.filter(isCritical);
  const toCheck = d.products.filter((p) => p.reviewFlags.length && !isCritical(p));
  // The collective confirmation is about content (products, prices as printed) — never allergens (DEC-058). It
  // covers the "to check" products too: their questions are accepted as read, an uncertain portion left out.
  const unconfirmed = d.products.filter((p) => !isCritical(p) && !p.contentConfirmed);
  const confirmed = d.products.filter((p) => !p.reviewFlags.length && p.contentConfirmed);
  const uncertainPortions = unconfirmed.filter((p) =>
    p.reviewFlags.some((f) => f.code === "portion_unclear"),
  );
  const productFixes = d.products.filter((p) => p.importSource?.autoFixes?.length);
  const pageFixes = job.files.flatMap((f) =>
    (f.quality?.fixes ?? []).map((x) => ({ page: f.page ?? f.position + 1, x })),
  );
  const fixCount =
    productFixes.reduce((n, p) => n + (p.importSource?.autoFixes?.length ?? 0), 0) + pageFixes.length;
  const retakePages = job.files.filter((f) => f.quality?.retake).length;
  const linkHost = job.sourceUrl ? new URL(job.sourceUrl).hostname.replace(/^www\./, "") : null;
  const failedFiles = job.files.filter((f) => f.status === "failed" || f.status === "blocked");
  const autoTr =
    d.products.filter((p) => p.translations.en?.status === "auto_unreviewed").length +
    d.categories.filter((c) => c.translations.en?.status === "auto_unreviewed").length;
  const blockers = preview.data?.blockers ?? [];
  const notices = blockers.filter((b) => b.acknowledgeable);
  const noticeOk = notices.every((b) => acks.has(b.code));
  const canPublish = !!preview.data?.canPublish && !busy && noticeOk;
  // Translations never block: unapproved ones are simply not published (the source language is shown).
  const left = exceptions.length + (unconfirmed.length ? 1 : 0);

  async function publish() {
    if (!preview.data || !menuId || publishing.current) return;
    // One click, one publication: a second tap while the first is on its way does nothing, and a retry of the same
    // draft revision reuses the idempotency key, so the server never publishes twice.
    publishing.current = true;
    if (!publishKey.current || publishKey.current.revision !== preview.data.draftRevision) {
      publishKey.current = { revision: preview.data.draftRevision, key: crypto.randomUUID() };
    }
    setBusy(true);
    try {
      await api.publish(
        menuId,
        preview.data.draftRevision,
        publishKey.current.key,
        notices.length > 0 && noticeOk,
      );
      await refresh();
      navigate("/app/publicat");
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "draft_changed")
        toast("Ciorna s-a schimbat. Verifică din nou.");
      else toast(e instanceof Error ? e.message : String(e));
      await refresh();
      setBusy(false);
    } finally {
      publishing.current = false;
    }
  }

  const noAllergens = d.products.filter((p) => !p.allergensConfirmed).length;

  return (
    <>
      {header}
      <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
        Am pregătit {plural(d.products.length, "produs", "produse")} în{" "}
        {plural(d.categories.length, "categorie", "categorii")}. Decizi doar ce contează pentru corectitudinea
        meniului; restul confirmi dintr-un clic. Nimic nu se publică până nu apeși „Publică meniul”.
      </p>
      <section className="card" aria-label="Rezumat">
        <div className="stat-grid">
          <div className="stat">
            <span className="v">{d.products.length}</span>
            <span className="k">produse identificate</span>
          </div>
          <div className="stat">
            <span className="v">{d.products.length - exceptions.length - toCheck.length}</span>
            <span className="k">gata, fără întrebări</span>
          </div>
          <div className="stat">
            <span className="v">{exceptions.length}</span>
            <span className="k">decizii necesare</span>
          </div>
          <div className="stat">
            <span className="v">{toCheck.length}</span>
            <span className="k">de verificat (opțional)</span>
          </div>
          <div className="stat">
            <span className="v">{fixCount}</span>
            <span className="k">corecturi automate</span>
          </div>
          {retakePages > 0 && (
            <div className="stat">
              <span className="v">{retakePages}</span>
              <span className="k">pagini greu de citit</span>
            </div>
          )}
        </div>
      </section>
      <RetakePages rid={rid} job={job} />

      {exceptions.length > 0 ? (
        <section className="card stack" aria-labelledby="v-exc" style={{ gap: 12 }}>
          <h2 id="v-exc" className="h2">
            Decizii necesare înainte de publicare ({exceptions.length})
          </h2>
          <span className="muted" style={{ fontSize: 14 }}>
            {linkHost
              ? `Prețurile au fost luate de pe ${linkHost}. Aplicațiile de livrare au adesea prețuri mai mari decât în restaurant: confirmă-le sau corectează-le dintr-un clic.`
              : "Prețuri pe care citirile nu le-au confirmat. Compară cu pagina și alege dintr-un clic."}
          </span>
          <ul className="verify-list">
            {exceptions.map((p) => (
              <ExceptionRow
                key={p.id}
                p={p}
                categoryName={catName.get(p.categoryId) ?? ""}
                menuId={menuId}
                busy={busy}
                onAct={act}
                linkHost={linkHost}
              />
            ))}
          </ul>
        </section>
      ) : (
        <Alert tone="success" title="Nicio decizie necesară">
          Toate prețurile au fost confirmate de cel puțin două citiri sau rezolvate de tine.
        </Alert>
      )}

      {toCheck.length > 0 && (
        <section className="card stack" aria-labelledby="v-chk" style={{ gap: 12 }}>
          <h2 id="v-chk" className="h2">
            De verificat — nu blochează publicarea ({toCheck.length})
          </h2>
          <span className="muted" style={{ fontSize: 14 }}>
            Nume citite în două feluri, gramaje nesigure, posibile duplicate. Le poți rezolva acum sau mai
            târziu; la confirmarea colectivă rămân cum au fost citite, iar gramajele nesigure nu se afișează
            până nu le confirmi.
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: "flex-start" }}
            aria-expanded={showImportant}
            onClick={() => setShowImportant(!showImportant)}
          >
            {showImportant ? "Ascunde lista" : "Vezi lista"}
          </button>
          {showImportant && (
            <ul className="verify-list">
              {toCheck.map((p) => (
                <ExceptionRow
                  key={p.id}
                  p={p}
                  categoryName={catName.get(p.categoryId) ?? ""}
                  menuId={menuId}
                  busy={busy}
                  onAct={act}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {fixCount > 0 && (
        <section className="card stack" aria-labelledby="v-fix" style={{ gap: 12 }}>
          <h2 id="v-fix" className="h2">
            Corecturi făcute automat ({fixCount})
          </h2>
          <span className="muted" style={{ fontSize: 14 }}>
            Diacritice, scrieri citite diferit, subtitluri și rânduri de descriere așezate unde le e locul.
            Nimic nu a fost inventat; poți modifica orice produs din editor.
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: "flex-start" }}
            aria-expanded={showFixes}
            onClick={() => setShowFixes(!showFixes)}
          >
            {showFixes ? "Ascunde istoricul" : "Vezi istoricul"}
          </button>
          {showFixes && (
            <ul className="verify-names">
              {pageFixes.map((f) => (
                <li key={`${f.page}-${f.x}`}>
                  <span className="muted">Pagina {f.page} · </span>
                  {f.x}
                </li>
              ))}
              {productFixes.flatMap((p) =>
                (p.importSource?.autoFixes ?? []).map((x) => (
                  <li key={`${p.id}-${x}`}>
                    <strong style={{ fontWeight: 600 }}>{p.name}</strong> <span className="muted">· {x}</span>
                  </li>
                )),
              )}
            </ul>
          )}
        </section>
      )}

      {failedFiles.length > 0 && (
        <section className="card stack" aria-labelledby="v-files" style={{ gap: 10 }}>
          <h2 id="v-files" className="h2">
            Fișiere necitite ({failedFiles.length})
          </h2>
          <ul className="verify-list">
            {failedFiles.map((f) => (
              <li key={f.id} className="verify-row">
                <span className="stack" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 600 }}>{f.name}</span>
                  <span className="caption muted">{f.error ?? ""}</span>
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ alignSelf: "flex-start" }}
            disabled={busy}
            onClick={() =>
              act(
                () => importApi(rid).retry(job.id),
                "Reîncercăm fișierele. Produsele noi apar în aceeași ciornă.",
              )
            }
          >
            <RefreshCw {...ICON} aria-hidden="true" />
            Reîncearcă fișierele
          </button>
        </section>
      )}

      <FrozenChecklist
        rid={rid}
        menuId={menuId}
        categories={d.categories}
        products={d.products}
        onChanged={refresh}
      />

      <section className="card stack" aria-labelledby="v-ok" style={{ gap: 12 }}>
        <h2 id="v-ok" className="h2">
          Confirmare colectivă
        </h2>
        <span className="muted" style={{ fontSize: 14 }}>
          {unconfirmed.length
            ? `${plural(unconfirmed.length, "produs de confirmat", "produse de confirmat")}${confirmed.length ? ` · ${confirmed.length} confirmate` : ""}`
            : `${plural(confirmed.length, "produs confirmat", "produse confirmate")}`}
        </span>
        {unconfirmed.length > 0 && (
          <>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: "flex-start" }}
              aria-expanded={showOk}
              onClick={() => setShowOk(!showOk)}
            >
              {showOk ? "Ascunde lista" : "Vezi produsele"}
            </button>
            {showOk && (
              <ul className="verify-names">
                {unconfirmed.map((p) => (
                  <li key={p.id}>
                    {p.name}{" "}
                    <span className="muted">
                      · {catName.get(p.categoryId)} ·{" "}
                      {p.variants
                        ? p.variants.map((v) => `${v.label} ${price(v.priceMinor, d.currency)}`).join(", ")
                        : price(p.priceMinor, d.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <label className="check-row">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>
                Am verificat aceste produse și prețurile lor așa cum apar în document și îmi asum publicarea
                lor{toCheck.length ? ", inclusiv a celor marcate „de verificat”, așa cum au fost citite" : ""}
                .
                {uncertainPortions.length
                  ? ` ${plural(uncertainPortions.length, "gramaj nesigur nu va fi afișat", "gramaje nesigure nu vor fi afișate")} până nu îl confirm.`
                  : ""}{" "}
                Confirmarea nu privește alergenii: îi completez pe fiecare produs sau clienții sunt îndrumați
                să întrebe personalul.
              </span>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start" }}
              disabled={!agree || busy}
              onClick={() =>
                act(
                  async () => {
                    await api.confirmProducts(menuId, d.menu.draftRevision);
                    setAgree(false);
                  },
                  `${plural(unconfirmed.length, "produs confirmat", "produse confirmate")}.`,
                )
              }
            >
              {unconfirmed.length === 1 ? "Confirmă produsul" : `Confirmă cele ${unconfirmed.length} produse`}
            </button>
          </>
        )}
      </section>

      {autoTr > 0 && (
        <section className="card stack" aria-labelledby="v-tr" style={{ gap: 12 }}>
          <h2 id="v-tr" className="h2">
            Traduceri în engleză
          </h2>
          <span className="muted" style={{ fontSize: 14 }}>
            {plural(autoTr, "traducere automată nerevizuită", "traduceri automate nerevizuite")}. Fără
            confirmare, meniul se publică în română; engleza apare doar după ce o confirmi.
          </span>
          <label className="check-row">
            <input type="checkbox" checked={agreeTr} onChange={(e) => setAgreeTr(e.target.checked)} />
            <span>Am verificat traducerile în engleză. Engleza poate apărea în meniul public.</span>
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ alignSelf: "flex-start" }}
            disabled={!agreeTr || busy}
            onClick={() =>
              act(async () => {
                await api.confirmTranslations(menuId, "en", d.menu.draftRevision);
                setAgreeTr(false);
              }, "Traducerile în engleză au fost confirmate. Engleza apare în meniul public după publicare.")
            }
          >
            Confirmă traducerile
          </button>
        </section>
      )}

      {notices.length > 0 && left === 0 && (
        <section className="card stack" aria-labelledby="v-al" style={{ gap: 10 }}>
          <h2 id="v-al" className="h2">
            Înainte de publicare
          </h2>
          {notices.map((b) => (
            <label key={b.code} className="check-row">
              <input
                type="checkbox"
                checked={acks.has(b.code)}
                onChange={(e) =>
                  setAcks((s) => {
                    const next = new Set(s);
                    if (e.target.checked) next.add(b.code);
                    else next.delete(b.code);
                    return next;
                  })
                }
              />
              <span>{b.text}</span>
            </label>
          ))}
          <Link
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: "flex-start" }}
            to={`/app/products?menu=${menuId}`}
          >
            Marchez produsele congelate și alergenii pe produse
          </Link>
        </section>
      )}

      <section className="verify-bar" aria-label="Publicare">
        <span className="stack" style={{ gap: 2, flex: "1 1 220px" }}>
          {canPublish ? (
            <>
              <strong style={{ fontWeight: 600 }}>
                Poți publica: {plural(d.products.length, "produs devine vizibil", "produse devin vizibile")}{" "}
                clienților, la link și la codul QR.
              </strong>
              {noAllergens > 0 && (
                <span className="caption muted">
                  {plural(noAllergens, "produs nu are", "produse nu au")} alergenii confirmați: clienții văd
                  alergenii estimați de Meniova, marcați „neconfirmați”, până îi verifici și confirmi.
                </span>
              )}
            </>
          ) : (
            <>
              <strong style={{ fontWeight: 600, display: "flex", gap: 6, alignItems: "center" }}>
                <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" />
                {left
                  ? `Mai ai ${plural(left, "pas", "pași")} înainte de publicare`
                  : !noticeOk
                    ? "Bifează confirmările de mai sus înainte de publicare."
                    : (blockers.find((b) => !b.acknowledgeable)?.text ?? "Nu este nimic de publicat.")}
              </strong>
              <span className="caption muted">
                {exceptions.length ? `${exceptions.length} de decis` : ""}
                {exceptions.length && unconfirmed.length ? " · " : ""}
                {unconfirmed.length ? `${unconfirmed.length} de confirmat` : ""}
              </span>
            </>
          )}
        </span>
        <button type="button" className="btn btn-secondary" onClick={() => openPreview(rid, menuId)}>
          <Eye {...ICON} aria-hidden="true" />
          Previzualizează
        </button>
        <button type="button" className="btn btn-primary" disabled={!canPublish} onClick={publish}>
          {busy ? "Se publică…" : "Publică meniul"}
        </button>
      </section>
    </>
  );
}
